/**
 * The incoming-call ringtone (doorbell live calls spec, "Member portal"): two
 * short tones every 3 s, synthesized with Web Audio — no audio file. Browsers
 * block audible playback until the person has interacted with the page, so
 * the banner rings only after the first interaction in this session
 * (`hasInteracted`); before that it shows silently.
 */

export interface ToneNode {
  connect(destination: unknown): unknown;
}
export interface ToneOscillator extends ToneNode {
  frequency: { value: number };
  start(when?: number): void;
  stop(when?: number): void;
}
export interface ToneGain extends ToneNode {
  gain: { value: number };
}
/** The subset of `AudioContext` the ringtone uses. */
export interface ToneContext {
  readonly currentTime: number;
  readonly destination: unknown;
  createOscillator(): ToneOscillator;
  createGain(): ToneGain;
  resume(): Promise<void>;
  close(): Promise<void>;
}
export interface Ringtone {
  start(): void;
  stop(): void;
  playing(): boolean;
}

export const RING_PERIOD_MS = 3_000;
const TONES: ReadonlyArray<readonly [number, number]> = [
  [0, 880],
  [0.4, 660],
];
const TONE_SECONDS = 0.3;
const VOLUME = 0.15;

export function createRingtone(makeContext: () => ToneContext | null): Ringtone {
  let ctx: ToneContext | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const burst = () => {
    const c = ctx;
    if (!c) return;
    const t = c.currentTime;
    for (const [offset, hz] of TONES) {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.frequency.value = hz;
      gain.gain.value = VOLUME;
      osc.connect(gain);
      gain.connect(c.destination);
      osc.start(t + offset);
      osc.stop(t + offset + TONE_SECONDS);
    }
  };

  return {
    start() {
      if (timer !== null) return;
      try {
        ctx = makeContext();
      } catch {
        ctx = null;
      }
      if (!ctx) return;
      ctx.resume().catch(() => {});
      burst();
      timer = setInterval(burst, RING_PERIOD_MS);
    },
    stop() {
      if (timer !== null) clearInterval(timer);
      timer = null;
      const c = ctx;
      ctx = null;
      c?.close().catch(() => {});
    },
    playing: () => timer !== null,
  };
}

let interacted = false;

export function markInteracted(): void {
  interacted = true;
}

type ActivationNavigator = { userActivation?: { hasBeenActive?: boolean } };

/** Whether this document may play sound: a pointer/key event seen, or the browser says so. */
export function hasInteracted(
  nav: ActivationNavigator | undefined = typeof navigator === "undefined" ? undefined : (navigator as unknown as ActivationNavigator),
): boolean {
  return interacted || nav?.userActivation?.hasBeenActive === true;
}

/** Called once in `main.tsx`; the login click already counts. */
export function installInteractionTracker(target: Pick<Window, "addEventListener" | "removeEventListener">): () => void {
  const off = () => {
    target.removeEventListener("pointerdown", on);
    target.removeEventListener("keydown", on);
  };
  function on() {
    markInteracted();
    off();
  }
  target.addEventListener("pointerdown", on);
  target.addEventListener("keydown", on);
  return off;
}

let shared: Ringtone | null = null;

/** One ringtone for the tab. */
export function getRingtone(): Ringtone {
  if (!shared) {
    shared = createRingtone(() => {
      if (typeof window === "undefined") return null;
      const Ctor =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      return Ctor ? (new Ctor() as unknown as ToneContext) : null;
    });
  }
  return shared;
}
